import { useState } from 'react';

import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';

interface WizardDeployStepProps {
  onComplete: (schedule: unknown) => void;
  onBack: () => void;
  onSaveAndExit?: () => void;
}

export const WizardDeployStep = ({ onComplete, onBack, onSaveAndExit }: WizardDeployStepProps) => {
  const [scheduleType, setScheduleType] = useState<'manual' | 'daily' | 'repeating' | 'cron'>(
    'manual'
  );
  const [dailyTime, setDailyTime] = useState('09:00');
  const [repeatInterval, setRepeatInterval] = useState(60);
  const [cronExpression, setCronExpression] = useState('0 0 * * *');

  const handleComplete = () => {
    let schedule = null;

    if (scheduleType === 'daily') {
      schedule = {
        type: 'daily',
        config: { time: dailyTime },
      };
    } else if (scheduleType === 'repeating') {
      schedule = {
        type: 'repeating',
        config: { intervalMinutes: repeatInterval },
      };
    } else if (scheduleType === 'cron') {
      schedule = {
        type: 'cron',
        config: { expression: cronExpression },
      };
    }

    onComplete(schedule);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Schedule & Deploy
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose how and when to run your integration
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <FormControl component="fieldset">
            <RadioGroup
              value={scheduleType}
              onChange={(e) => setScheduleType(e.target.value as typeof scheduleType)}
            >
              <FormControlLabel
                value="manual"
                control={<Radio />}
                label="Manual (run on-demand only)"
              />

              <FormControlLabel value="daily" control={<Radio />} label="Daily at specific time" />
              {scheduleType === 'daily' && (
                <Box sx={{ ml: 4, mt: 1, mb: 2 }}>
                  <TextField
                    type="time"
                    label="Time"
                    value={dailyTime}
                    onChange={(e) => setDailyTime(e.target.value)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              )}

              <FormControlLabel value="repeating" control={<Radio />} label="Repeating interval" />
              {scheduleType === 'repeating' && (
                <Box sx={{ ml: 4, mt: 1, mb: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Interval</InputLabel>
                    <Select
                      value={repeatInterval}
                      onChange={(e) => setRepeatInterval(Number(e.target.value))}
                      label="Interval"
                    >
                      <MenuItem value={15}>Every 15 minutes</MenuItem>
                      <MenuItem value={30}>Every 30 minutes</MenuItem>
                      <MenuItem value={60}>Every hour</MenuItem>
                      <MenuItem value={120}>Every 2 hours</MenuItem>
                      <MenuItem value={360}>Every 6 hours</MenuItem>
                      <MenuItem value={720}>Every 12 hours</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              )}

              <FormControlLabel value="cron" control={<Radio />} label="Custom cron expression" />
              {scheduleType === 'cron' && (
                <Box sx={{ ml: 4, mt: 1, mb: 2 }}>
                  <TextField
                    fullWidth
                    label="Cron Expression"
                    value={cronExpression}
                    onChange={(e) => setCronExpression(e.target.value)}
                    size="small"
                    helperText="Example: 0 0 * * * (daily at midnight)"
                  />
                </Box>
              )}
            </RadioGroup>
          </FormControl>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button variant="outlined" onClick={onBack}>
          Back
        </Button>
        <Button variant="contained" onClick={handleComplete}>
          Finish
        </Button>
        {onSaveAndExit && (
          <Button variant="outlined" onClick={onSaveAndExit}>
            Save and Exit
          </Button>
        )}
      </Box>
    </Box>
  );
};
